<template>
  <div class="main-content">
    <div class="card p-3">
      <div class="page-head">
        <div>
          <div class="page-title-big">Solicitações</div>
        </div>

        <div class="head-actions">
          <button
            class="btn btn-outline-primary btn-sm top-btn"
            @click="goApprovals"
          >
            <span class="btn-label">Aprovações</span>
            <span
              v-if="approvalCount > 0"
              class="badge badge-danger badge-count"
            >
              {{ approvalCount }}
            </span>
          </button>

          <button
            class="btn btn-outline-secondary btn-sm top-btn"
            @click="goMyRequests"
          >
            <span class="btn-label">Minhas solicitações</span>
          </button>

          <button class="btn btn-primary btn-sm top-btn" @click="goNew">
            <span class="btn-label">Nova solicitação</span>
          </button>
        </div>
      </div>

      <div class="filters-box">
        <div class="filters-row">
          <input
            v-model="filters.search"
            class="form-control"
            placeholder="Buscar por ID, título, tag ou solicitante..."
            @keyup.enter="doSearch"
          />

          <select v-model="filters.status" class="form-control select-filter">
            <option value="">Todos os status</option>
            <option value="new">Novo</option>
            <option value="in_progress">Em andamento</option>
            <option value="waiting_approval">Aguardando aprovação</option>
            <option value="done">Concluído</option>
            <option value="rejected">Reprovado</option>
            <option value="cancelled">Cancelado</option>
          </select>

          <input
            class="form-control area-locked"
            :value="currentUserDepartmentName"
            :title="currentUserDepartmentName"
            disabled
          />

          <button class="btn btn-primary btn-filter" @click="doSearch">
            Buscar
          </button>

          <button class="btn btn-light btn-filter" @click="clearFilters">
            Limpar
          </button>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger mt-2">
        {{ error }}
      </div>

      <div class="table-shell mt-3">
        <div v-if="loading" class="empty-row">Carregando...</div>

        <div v-else class="table-responsive">
          <table class="table table-sm table-bordered my-table">
            <thead>
              <tr>
                <th class="col-id">ID</th>
                <th>TÍTULO</th>
                <th>TAG</th>
                <th>SOLICITANTE</th>
                <th class="col-step">ETAPA ATUAL</th>
                <th class="col-area">DEPARTAMENTO ATUAL</th>
                <th class="col-area">ATRIBUÍDO</th>
                <th class="col-status">STATUS</th>
                <th class="col-open">ABERTURA</th>
                <th class="actions-th col-actions">AÇÕES</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="w in filteredRows" :key="w.id">
                <td class="id-cell">
                  <b>#{{ w.id }}</b>
                </td>

                <td class="title-cell">
                  <div class="title-main">{{ w.title || "—" }}</div>
                </td>

                <td>{{ w.tag || "—" }}</td>

                <td>{{ requesterName(w) }}</td>

                <td>
                  <span class="pill-step">
                    {{ currentStepName(w) }}
                  </span>
                </td>

                <td>{{ currentDepartmentName(w) }}</td>

                <td>
                  <span v-if="currentResponsibleName(w)">
                    {{ currentResponsibleName(w) }}
                  </span>
                  <span v-else class="text-muted" title="Ninguém pegou ainda">
                    —
                  </span>
                </td>

                <td>
                  <span class="badge-status" :class="'st-' + displayStatus(w)">
                    {{ statusLabel(displayStatus(w)) }}
                  </span>
                </td>

                <td>{{ formatDate(w.created_at) }}</td>

                <td class="actions-td">
                  <div class="actions">
                    <button
                      class="icon-btn btn-progress"
                      title="Ver andamento"
                      @click="viewProgress(w)"
                    >
                      <i class="fa fa-eye"></i>
                    </button>
                    <button
                      class="icon-btn btn-view"
                      title="Trabalhar na solicitação"
                      @click="view(w)"
                    >
                      <i class="fa fa-pencil-alt"></i>
                    </button>
                    <button
                      v-if="canCancel(w)"
                      class="icon-btn btn-del"
                      title="Cancelar fluxo"
                      @click="cancelFlow(w)"
                    >
                      <i class="fa fa-ban"></i>
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="!filteredRows.length">
                <td colspan="10" class="empty-row">
                  Nenhum registro encontrado.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      filters: {
        search: "",
        status: "",
      },
      rows: [],
      users: [],
      usersById: {},
      departments: [],
      departmentsById: {},
      loading: false,
      error: "",
      apiDepartmentNames: [],
      apiDepartmentIds: [],
    };
  },

  async mounted() {
    await this.getUsers();
    this.getDepartments();
    await this.fetchRows();
  },

  computed: {
    currentUser() {
      return this.$store?.state?.user || {};
    },

    currentUserId() {
      const u = this.currentUser;
      return u.id || u.user?.id || null;
    },

    currentUserDepartmentName() {
      if (this.apiDepartmentNames.length) {
        return this.apiDepartmentNames.join(" | ");
      }
      const foundUser = this.users.find(
        (x) => Number(x.id) === Number(this.currentUserId)
      );
      return (
        this.resolveDepartmentName(foundUser) ||
        this.resolveDepartmentName(this.currentUser) ||
        "—"
      );
    },

    filteredRows() {
      return this.rows.filter(
        function (w) {
          if (!this.filters.status) return true;
          return this.displayStatus(w) === this.filters.status;
        }.bind(this)
      );
    },

    approvalCount() {
      return this.rows.filter(
        function (w) {
          return this.displayStatus(w) === "waiting_approval";
        }.bind(this)
      ).length;
    },
  },

  methods: {
    resolveDepartmentId(source) {
      if (!source || typeof source !== "object") return null;

      if (source.department_id) return Number(source.department_id);
      if (source.department?.id) return Number(source.department.id);

      if (Array.isArray(source.departments) && source.departments.length) {
        const firstDept = source.departments[0];
        if (firstDept?.id) return Number(firstDept.id);
      }

      if (
        Array.isArray(source.user_departments) &&
        source.user_departments.length
      ) {
        const firstUserDept = source.user_departments[0];
        if (firstUserDept?.department_id) {
          return Number(firstUserDept.department_id);
        }
        if (firstUserDept?.department?.id) {
          return Number(firstUserDept.department.id);
        }
      }

      if (source.user) {
        return this.resolveDepartmentId(source.user);
      }

      return null;
    },

    resolveDepartmentName(source) {
      if (!source || typeof source !== "object") return null;

      if (source.department?.name) return source.department.name;
      if (source.department?.description) return source.department.description;
      if (source.department_name) return source.department_name;
      if (source.department_description) return source.department_description;

      if (Array.isArray(source.departments) && source.departments.length) {
        const firstDept = source.departments[0];
        if (firstDept?.name) return firstDept.name;
        if (firstDept?.description) return firstDept.description;
      }

      if (
        Array.isArray(source.user_departments) &&
        source.user_departments.length
      ) {
        const firstUserDept = source.user_departments[0];
        if (firstUserDept?.department?.name)
          return firstUserDept.department.name;
        if (firstUserDept?.department?.description) {
          return firstUserDept.department.description;
        }
      }

      if (source.user) {
        return this.resolveDepartmentName(source.user);
      }

      return null;
    },

    goNew() {
      this.$router.push("/Flow/new");
    },

    goApprovals() {
      this.$router.push("/Flow/approvals");
    },

    goMyRequests() {
      this.$router.push("/Flow/my");
    },

    doSearch() {
      this.fetchRows();
    },

    clearFilters() {
      this.filters.search = "";
      this.filters.status = "";
      this.fetchRows();
    },

    formatDate(iso) {
      if (!iso) return "—";

      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;

      return d.toLocaleString("pt-BR");
    },

    async getUsers() {
      const api = this.$store.state.api + "users";

      const attempts = [
        { paginated: false, with: ["departments"] },
        { paginated: false, with: ["department"] },
        { paginated: false },
      ];

      for (const params of attempts) {
        try {
          const res = await axios.get(api, {
            params,
            timeout: 10000,
          });

          this.users = Array.isArray(res.data?.data)
            ? res.data.data
            : res.data || [];

          this.usersById = this.users.reduce((acc, u) => {
            acc[u.id] = u.name || u.username || `#${u.id}`;
            return acc;
          }, {});

          return;
        } catch (e) {
          console.error("TENTATIVA USERS FALHOU:", params, e);
        }
      }

      this.users = [];
      this.usersById = {};
    },

    async getDepartments() {
      try {
        const api = this.$store.state.api + "departments?paginated=false";
        const res = await axios.get(api, {
          timeout: 10000,
        });

        this.departments = Array.isArray(res.data?.data)
          ? res.data.data
          : res.data || [];

        this.departmentsById = this.departments.reduce((acc, d) => {
          acc[d.id] = d.name || d.description || `#${d.id}`;
          return acc;
        }, {});
      } catch (e) {
        console.error("ERRO DEPARTMENTS:", e);
        this.departments = [];
        this.departmentsById = {};
      }
    },

    async fetchRows() {
      this.loading = true;
      this.error = "";

      try {
        const api = this.$store.state.api + "flows";
        const params = {};

        if (this.filters.search?.trim()) {
          params.search = this.filters.search.trim();
        }

        const res = await axios.get(api, {
          params,
          timeout: 10000,
        });

        this.rows = Array.isArray(res.data?.data)
          ? res.data.data
          : res.data || [];

        if (Array.isArray(res.data?.current_user_department_names)) {
          this.apiDepartmentNames = res.data.current_user_department_names;
        }
        if (Array.isArray(res.data?.current_user_department_ids)) {
          this.apiDepartmentIds = res.data.current_user_department_ids;
        }
      } catch (e) {
        console.error("ERRO FLOWS:", e);
        this.error = "Erro ao carregar a fila de trabalho.";
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },

    departmentName(deptId) {
      if (!deptId) return "—";
      return this.departmentsById[deptId] || `#${deptId}`;
    },

    userName(userId) {
      if (!userId) return "—";
      return this.usersById[userId] || `#${userId}`;
    },

    requesterName(w) {
      if (w.opened_by_name) return w.opened_by_name;

      if (w.openedBy && (w.openedBy.name || w.openedBy.username)) {
        return w.openedBy.name || w.openedBy.username;
      }

      if (w.opened_by_user_id) {
        return this.userName(w.opened_by_user_id);
      }

      return "—";
    },

    currentTransition(w) {
      if (!Array.isArray(w.transitions)) return null;

      return w.transitions.find(
        (t) => Number(t.step_order) === Number(w.current_step_order)
      );
    },

    currentStepName(w) {
      if (w.current_step_name) return w.current_step_name;

      const current = this.currentTransition(w);

      if (current && current.name) {
        return current.name;
      }

      if (w.current_step_order) {
        return `Etapa ${w.current_step_order}`;
      }

      return "—";
    },

    currentDepartmentId(w) {
      const current = this.currentTransition(w);

      if (current?.department_id) {
        return current.department_id;
      }

      return null;
    },

    currentDepartmentName(w) {
      if (w.current_department_name) return w.current_department_name;

      const current = this.currentTransition(w);

      if (current) {
        if (current.department && current.department.name) {
          return current.department.name;
        }

        if (current.department_id) {
          return this.departmentName(current.department_id);
        }
      }

      return "—";
    },

    currentResponsibleName(w) {
      const current = this.currentTransition(w);

      if (current && current.responsible && current.responsible.name) {
        return current.responsible.name;
      }

      return "";
    },

    flowStatus(w) {
      return w.status || "new";
    },

    displayStatus(w) {
      var rawStatus = this.flowStatus(w);

      if (rawStatus === "cancelled") return "cancelled";
      if (rawStatus === "rejected") return "rejected";
      if (rawStatus === "waiting_approval") return "waiting_approval";
      if (rawStatus === "new") return "new";

      if (rawStatus === "done") {
        if (this.hasRealFinalization(w)) {
          return "done";
        }
        return "in_progress";
      }

      return rawStatus || "in_progress";
    },

    hasRealFinalization(w) {
      if (!w) return false;

      if (
        w.finished_at ||
        w.finishedAt ||
        w.completed_at ||
        w.completedAt ||
        w.closed_at ||
        w.closedAt ||
        w.finalized_at ||
        w.finalizedAt
      ) {
        return true;
      }

      if (
        w.finished_by_user_id ||
        w.completed_by_user_id ||
        w.closed_by_user_id ||
        w.finalized_by_user_id
      ) {
        return true;
      }

      return false;
    },

    statusLabel(status) {
      const map = {
        new: "Nova",
        in_progress: "Em andamento",
        waiting_approval: "Aguardando aprovação",
        rejected: "Reprovado",
        done: "Concluído",
        cancelled: "Cancelado",
      };

      return map[status] || status || "—";
    },

    canCancel(w) {
      const st = this.displayStatus(w);
      if (["done", "rejected", "cancelled"].includes(st)) return false;
      const userId = Number(this.currentUserId);
      const isOwner = Number(w.opened_by_user_id) === userId;
      const isAdmin = Number(this.$store?.state?.user?.perfil_id) === 8;
      const myDeptIds = this.apiDepartmentIds.map(Number);
      const openerDeptIds = (w.opener_department_ids || []).map(Number);
      const isOpenerDept = myDeptIds.some((id) => openerDeptIds.includes(id));
      return isOwner || isOpenerDept || isAdmin;
    },

    async cancelFlow(w) {
      const confirm = await swal({
        title: "Cancelar fluxo?",
        text: `Tem certeza que deseja cancelar "${
          w.title || "#" + w.id
        }"? Esta ação não pode ser desfeita.`,
        icon: "warning",
        buttons: ["Não", "Sim, cancelar"],
        dangerMode: true,
      });

      if (!confirm) return;

      try {
        const api = this.$store.state.api;
        await this.$http.post(api + "flows/" + w.id + "/cancel");
        swal("Cancelado", "O fluxo foi cancelado com sucesso.", "success");
        await this.fetchRows();
      } catch (e) {
        swal(
          "Erro",
          e?.response?.data?.message || "Erro ao cancelar o fluxo.",
          "error"
        );
      }
    },

    view(w) {
      this.$router.push(`/Flow/view/${w.id}`);
    },

    viewProgress(w) {
      this.$router.push(`/Flow/inprogressView/${w.id}`);
    },

    edit(w) {
      this.$router.push(`/Flow/edit/${w.id}`);
    },
  },
};
</script>

<style scoped>
.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.page-title-big {
  font-size: 32px;
  font-weight: 800;
  color: #374151;
  line-height: 1.1;
}

.page-subtitle {
  font-size: 15px;
  color: #6b7280;
  margin-top: 6px;
}

.head-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.top-btn {
  min-width: 165px;
  height: 32px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
}

.btn-label {
  display: inline-block;
  line-height: 1;
}

.badge-count {
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  line-height: 1;
}

.filters-box {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 14px;
}

.filters-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filters-row .form-control {
  height: 42px;
  border-radius: 8px;
}

.select-filter {
  width: 220px;
  max-width: 220px;
}

.area-locked {
  flex: 1;
  min-width: 290px;
  background: #f9fafb;
  color: #6b7280;
  font-weight: 600;
  cursor: default;
}

.btn-filter {
  height: 42px;
  min-width: 110px;
  border-radius: 8px;
  padding: 0 18px;
  font-weight: 700;
}

.table-shell {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 10px;
}

.my-table thead th {
  background: #f6f7fb;
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.03em;
  border-color: #e5e7eb;
}

.my-table td {
  vertical-align: middle;
  border-color: #e5e7eb;
  font-weight: 600;
  color: #4b5563;
}

.col-id {
  width: 110px;
}
.col-status {
  width: 200px;
}
.col-step {
  width: 220px;
}
.col-area {
  width: 220px;
}
.col-open {
  width: 170px;
}
.col-actions {
  width: 90px;
  text-align: center;
}

.id-cell {
  font-weight: 900;
  color: #6b7280;
}

.title-main {
  font-weight: 700;
  color: #374151;
}

.empty-row {
  text-align: center;
  padding: 18px !important;
  color: #9ca3af;
  font-weight: 700;
}

.badge-status {
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
  border: 1px solid #d1d5db;
}

.st-new {
  background: #e8f0ff;
  color: #1d4ed8;
}

.st-in_progress {
  background: #fff7e6;
  color: #a16207;
}

.st-waiting_approval {
  background: #f3e8ff;
  color: #7c3aed;
}

.st-rejected {
  background: #ffe3e3;
  color: #b91c1c;
}

.st-done {
  background: #dcfce7;
  color: #15803d;
}

.pill-step {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.actions-th,
.actions-td {
  text-align: center;
  white-space: nowrap;
}

.actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.icon-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 6px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid #ccc;
  margin: 0;
  line-height: 1;
}

.icon-btn i {
  font-size: 12px;
  color: #111;
}

.btn-view {
  border-color: #f1c21b;
}

.btn-edit {
  border-color: #24a148;
}

.icon-btn:hover {
  filter: brightness(0.95);
  transform: translateY(-1px);
  transition: 0.12s;
}

@media (max-width: 992px) {
  .page-head {
    flex-direction: column;
  }

  .head-actions {
    flex-wrap: wrap;
  }

  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .select-filter,
  .area-locked,
  .btn-filter {
    max-width: 100%;
    width: 100%;
    min-width: 100%;
  }
}
</style>
